import React, { useEffect, useCallback, useState } from 'react';
import PropTypes from 'prop-types';

import useRequest from 'core/hooks/useRequest';
import { bebop as bebopApi } from 'core/api/definitions/bebop';

import withoutSSR from 'core/components/withoutSSR';
import CoreLoader from 'core/components/Loader';

import { REACTIONS_EMOJI_LS_KEY, PARENT_TYPE_MAP } from './constants';
import {
  getReactionFromLS,
  checkReactionFromLSExpired,
  updateReactionInLS,
  updateExpiredReactionInLS,
} from './utils';

import EmojiReactions, { EmojiLoader } from './EmojiReactions';

import { VOID_QUERY } from 'core/site/constants';


function Reactions(props) {
  const {
    targetId,
    reactions: reactionsProp,
    controls: Controls,
    targetType,
    parentType,
    parentId,
    lsKey,
    customLoader: Loader = CoreLoader,
    ...otherProps
  } = props;

  const queryParams = reactionsProp
    ? VOID_QUERY
    : {
      queryKey: ['bebopApi', 'getReactions', {
        target_uuid: targetId,
        target_type: targetType,
        parent_id: parentId,
        parent_type: parentType,
      }],
      queryFn: ({ queryKey: [, method, params] }) => bebopApi[method](params).catch(() => ({})),
      select: ({ data }) => data[targetId] || data,
    };

  const {
    data: baseReactions,
    isLoading,
  } = useRequest(queryParams);

  const [reactions, updateReactionsFromDb] = useState();
  const [reactedReaction, setReactedReaction] = useState();
  const [awaiting, setAwait] = useState(false);

  const handleReacted = useCallback(() => {
    const isExpired = checkReactionFromLSExpired(targetId, lsKey);
    if (isExpired) {
      updateExpiredReactionInLS(targetId, reactions, lsKey);
    }

    const reaction = getReactionFromLS(targetId, lsKey);
    setReactedReaction(reaction || null);
  }, [lsKey, reactions, targetId]);

  useEffect(() => {
    const handleStorage = event => event.key === lsKey && handleReacted();

    handleReacted();
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [handleReacted, lsKey]);

  useEffect(() => {
    updateReactionsFromDb(baseReactions);
  }, [baseReactions]);

  const submitReaction = useCallback(({ value }) => {
    setAwait(true);
    bebopApi
      .submitReaction({
        target_uuid: targetId,
        target_type: targetType,
        parent_uuid: parentId,
        parent_type: parentType,
        reaction_type: value,
      })
      .then(({ data, meta }) => {
        updateReactionInLS(targetId, {
          id: targetId,
          reacted: value,
          values: data[targetId],
          meta,
        }, lsKey);
        updateReactionsFromDb({ meta, ...data[targetId] });
        handleReacted();
      }).then(() => setAwait(false));
  }, [targetId, targetType, parentId, parentType, lsKey, handleReacted]);

  const deleteReaction = useCallback(() => {
    if (!reactedReaction?.meta) return;

    const { uuid, hash_code: hashCode } = reactedReaction.meta;

    bebopApi
      .deleteReaction({
        uuid: uuid,
        hash_code: hashCode,
      })
      .then(({ data }) => {
        updateReactionInLS(targetId, {
          id: targetId,
          values: data[targetId],
        }, lsKey);
        updateReactionsFromDb(data[targetId] || data);
        handleReacted();
      });
  }, [handleReacted, lsKey, reactedReaction, targetId]);

  const handleReactions = event => {
    const { value, checked } = event.target;

    if (checked) {
      submitReaction({ value });
    } else {
      deleteReaction();
    }
  };

  if (isLoading) return <Loader title={otherProps.title} />;
  if (!reactions) return null;

  return (
    <Controls
      awaiting={awaiting}
      targetId={targetId}
      reactions={reactions}
      sumbitReactions={handleReactions}
      reactedReaction={reactedReaction}
      {...otherProps}
    />
  );
}

Reactions.defaultProps = {
  controls: EmojiReactions,
  targetType: 'Widgets::StoryCard',
  parentType: 'Topics::Story',
  lsKey: REACTIONS_EMOJI_LS_KEY,
};

/**
 * TODO:
 *  Описание параметров для работы с апишкой, должно быть описано в документации к API.
 *  После того как появится такая документация,
 *  можно будет убрать ПОДРОБНОЕ описание параметров работы с API:
 *  targetId, targetType, parentId, parentType.
 */
Reactions.propTypes = {
  /**
   * Идентификатор от чего пришла реакция.
   *
   * В стори, это ID карточки-виджета.
   * В обычном топике это ID топика.
   */
  targetId: PropTypes.string.isRequired,

  /**
   * @ignore
   * Уточнение, с каким типом реакции работаем: Реакции с эмодзи, Голосование, Рейтинг и т.д.
   * В стори это тип виджета.
   * В обычном топике это тип топика.
   */
  targetType: PropTypes.string,

  /**
   * @ignore
   * В стори это ID топика.
   * В обычном топике тут ничего не будет.
   *
   * Обозначение, к какому топику принадлежит виджет, в котором используются реакции.
   * Используется для сбора статистики по топику.
   */
  parentId: PropTypes.string,

  /**
   * @ignore
   * Тип топика.
   * Требуется для сбора статистики по реакциям, в разрезе топика.
   */
  parentType: PropTypes.string,

  /**
   * Счетчики реакций
   */
  reactions: PropTypes.object,

  /**
   * Компонент с контролами, для отправки реакций.
   * API реакций это отдельный микросервис в рамках bebop API.
   * Поддерживает разные типы реакций: Реакции с эмодзи, Голосование, Рейтинг и т.д.
   */
  controls: PropTypes.func,

  /**
   * loader должен совпадать по стилизации с controls (по умолчанию это EmojiReactions)
   */
  customLoader: PropTypes.func,

  /**
   * Ключ в localStorage.
   *
   * В рамках одного топика может быть несколько разных реакций:
   * например в сторисах это EmojiReactions и Rating или Voting.
   * Технически на странице могут быть хоть все типы реакций.
   *
   * Для этих целей можно указать уникальный ключ в storage, чтобы не словить конфликт.
   * Конфликт может быть, если в рамках одного targetId (топика)
   * будут лежать одинаковые emoji slug от разных реакций.
   */
  lsKey: PropTypes.string,
};

export { EmojiLoader };

const ReactionsWithHOCs = withoutSSR(Reactions);

ReactionsWithHOCs.displayName = 'Reactions';

export { PARENT_TYPE_MAP };
export { Reactions as StorybookComponent };
export default ReactionsWithHOCs;
